import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import UploadDocumentInteractor from "../../interactor/document/UploadDocumentInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import UpdateDocumentInteractor from "../../interactor/document/UpdateDocumentInteractor";
import { IReportingPeriodApplicantDocument } from "../../model/document/ReportingPeriodApplicantDocument";
import GetReportingPeriodApplicantDocumentsInteractor from "../../interactor/document/GetReportingPeriodApplicantDocumentsInteractor";
import AssignReportingPeriodApplicantDocumentInteractor from "../../interactor/document/AssignReportingPeriodApplicantDocumentInteractor";
import RemoveReportingPeriodApplicantDocumentInteractor from "../../interactor/document/RemoveReportingPeriodApplicantDocumentInteractor";

export interface TReportingPeriodApplicantDocumentsPresenter extends TLoadingAwarePresenter {
  documents: IReportingPeriodApplicantDocument[];
  reportingPeriodApplicantId: number;
  allowedFileTypes?: string;
}

export interface IReportingPeriodApplicantDocumentsPresenter
  extends TReportingPeriodApplicantDocumentsPresenter, TPresentable {
  setInitialData(getInitialData: IInitialData): void;
  assignDocumentToForm(
    formVersionDocument: IReportingPeriodApplicantDocument
  ): void;
  updateDocument(formVersionDocument: IReportingPeriodApplicantDocument): void;
  removeDocument(formVersionDocument: IReportingPeriodApplicantDocument): void;
}

interface IInitialData {
  documents?: IReportingPeriodApplicantDocument[];
  allowedFileTypes?: string;
}

const defaultState: TReportingPeriodApplicantDocumentsPresenter = {
  documents: [],
  reportingPeriodApplicantId: 0
};

const ReportingPeriodApplicantDocumentsPresenter = withStore<
  IReportingPeriodApplicantDocumentsPresenter,
  TReportingPeriodApplicantDocumentsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IReportingPeriodApplicantDocumentsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state =
      _store.getState<TReportingPeriodApplicantDocumentsPresenter>();

    loader.start("documentLoader");

    const setInitialData = (initialData: IInitialData) => {
      _store.update({
        ...initialData
      });
      loader.stop("documentLoader");
    };

    const getDocuments = async () => {
      const { reportingPeriodApplicantId } =
        _store.getState<IReportingPeriodApplicantDocumentsPresenter>();
      const documents = await _application.container
        .resolve<GetReportingPeriodApplicantDocumentsInteractor>(
          "getReportingPeriodApplicantDocuments"
        )
        .execute(reportingPeriodApplicantId);
      _store.update({ documents });
    };

    const uploadDocument = async (document: any) => {
      return _application.container
        .resolve<UploadDocumentInteractor>("uploadDocument")
        .execute(document);
    };

    const assignDocumentToForm = async (
      reportingPeriodApplicantDocument: IReportingPeriodApplicantDocument
    ) => {
      try {
        loader.start("documentLoader");
        const { reportingPeriodApplicantId } =
          _store.getState<IReportingPeriodApplicantDocumentsPresenter>();
        let documentId = await uploadDocument(
          reportingPeriodApplicantDocument.document
        );
        if (documentId) {
          reportingPeriodApplicantDocument.documentId = documentId;
          reportingPeriodApplicantDocument.reportingPeriodApplicantId =
            reportingPeriodApplicantId;
          await _application.container
            .resolve<AssignReportingPeriodApplicantDocumentInteractor>(
              "assignReportingPeriodApplicantDocument"
            )
            .execute(reportingPeriodApplicantDocument);
          _application.container
            .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
            .execute(
              translate(
                "form_version_document_form.messages.new_document_success"
              )
            );
        }
        await getDocuments();
        loader.stop("documentLoader");
      } catch (error) {
        loader.stop("documentLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message, false);
      }
    };

    const updateDocument = async (
      reportingPeriodApplicantDocument: IReportingPeriodApplicantDocument
    ) => {
      try {
        loader.start("documentLoader");
        await _application.container
          .resolve<UpdateDocumentInteractor>("updateDocument")
          .execute(
            reportingPeriodApplicantDocument.documentId,
            reportingPeriodApplicantDocument.document
          );
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "form_version_document_form.messages.document_update_success"
            )
          );
        await getDocuments();
        loader.stop("documentLoader");
      } catch (error) {
        loader.stop("documentLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const removeDocument = async (
      reportingPeriodApplicantDocument: IReportingPeriodApplicantDocument
    ) => {
      try {
        loader.start("documentLoader");
        await _application.container
          .resolve<RemoveReportingPeriodApplicantDocumentInteractor>(
            "removeReportingPeriodApplicantDocument"
          )
          .execute(
            reportingPeriodApplicantDocument.reportingPeriodApplicantId,
            reportingPeriodApplicantDocument.documentId
          );
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "form_version_document_form.messages.remove_document_success"
            )
          );
        await getDocuments();
        loader.stop("documentLoader");
      } catch (error) {
        loader.stop("documentLoader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setInitialData,
      assignDocumentToForm,
      updateDocument,
      removeDocument
    };
  },
  defaultState
);

export default ReportingPeriodApplicantDocumentsPresenter;
